import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, typography } = themeVariables;

/**
 * Width at which the profile is wide enough to seat the portrait beside the copy
 * rather than above it. Measured against the space the profile is given, so it
 * holds whether the profile is one to a row or two.
 */
const SIDE_BY_SIDE_WIDTH = 600;

/**
 * Establishes the container that `partnerLayout` measures itself against, and
 * carries no layout of its own.
 *
 * It is a separate element because a container query resolves against the nearest
 * *ancestor* container and never against the element declaring `container-type`.
 * Were the grid on this element, its query would skip past to whatever container
 * the block sits in and reflow at the wrong width. Keeping the container here also
 * means the profile answers to its own width wherever it is used, rather than
 * relying on a parent to provide one.
 */
export const partner = css`
  container-type: inline-size;
`;

export const partnerLayout = css`
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  gap: var(${spacing.lg});

  @container (min-width: ${SIDE_BY_SIDE_WIDTH}px) {
    /* Portrait beside the copy, with the products spanning both beneath. */
    grid-template-columns: 1fr 1fr;
    column-gap: var(${spacing.xl});
  }
`;

export const partnerImage = css`
  display: block;
  width: 100%;
  height: auto;
  /* The portrait is square, so reserve its box before it loads. */
  aspect-ratio: 1;
  border-radius: var(${borderRadius.md.desktop});
  object-fit: cover;
`;

export const partnerCopy = css`
  align-self: center;
`;

export const partnerHeading = css`
  margin: 0 0 var(${spacing.md}) 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});
  font-size: var(${typography.heading.sizes.xs.fontSize});
  line-height: var(${typography.heading.sizes.xs.lineHeight});
  letter-spacing: var(${typography.heading.letterSpacing});
  color: var(${colors.content.default});
`;

export const partnerBody = css`
  margin: 0 0 var(${spacing.md}) 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.default});
`;

/* The same treatment the design system gives a link inside a block (see
   `ProductCarousel`'s subheading link), rather than the black semibold text this
   was before: there is no body-semibold token to carry that weight across, and the
   link colour is what marks it as the way out to the storefront. */
export const partnerCta = css`
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.link});

  &:focus-visible {
    outline: 2px solid var(${colors.stroke.focus});
    outline-offset: 2px;
  }
`;

export const products = css`
  /* Always two up. A partner shows exactly two products, and they are a pair:
     splitting them across rows would read as a list that had been cut short. */
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(${spacing.md});

  @container (min-width: ${SIDE_BY_SIDE_WIDTH}px) {
    grid-column: 1 / -1;
  }
`;
